import { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { BusinessAccountingForm } from "../../../../../../components/form/business/accounting";
import { useGetBusiness } from "../../../../../../hooks/api/business/useGetBusiness";
import { usePatchAdditionalBusinessDetails } from "../../../../../../hooks/api/business/usePatchAdditionalBusinessDetails";
import { usePatchBusiness } from "../../../../../../hooks/api/business/usePatchBusiness";
import { usePostAdditionalBusinessDetails } from "../../../../../../hooks/api/business/usePostAdditionalBusinessDetails";
import WsSnackbar, { WsSnackbarTypes } from "../../../../../../components/ws-snackbar";
import { Feedback } from "../../../../../../@types/enums/general/Feedback";
import { useTranslation } from "@onefront/react-sdk";

export const BusinessAccounting = ({ setBusiness }) => {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const location = useLocation();
    const { workspaceId, businessId } = useParams();
    const [accountingConfiguration, setAccountingConfiguration] = useState<any>({
        accountingType: "ORDINARY",
        simplifiedAccountingType: null,
        vatReportsType: null
    });
    const [asset, setAsset] = useState<any>({
        assetGroup: null,
        assetSpecies: null,
        statutoryDepreciation: true
    });
    const [isEditing, setIsEditing] = useState(false);
    const [feedback, setFeedback] = useState<any>(null);

    const { data: businessData, loading: loadingGet, fetch: getBusiness } = useGetBusiness(workspaceId, businessId);

    const { loading: loadingPostAccounting, fetch: postBusinessAccounting } = usePostAdditionalBusinessDetails(
        workspaceId,
        businessId,
        "accounting-configuration"
    );

    const { loading: loadingPatchAccounting, fetch: patchBusinessAccounting } = usePatchAdditionalBusinessDetails(
        workspaceId,
        businessId,
        "accounting-configuration",
        businessData?.accountingConfiguration?.id
    );

    const { loading: loadingPatchBusiness, fetch: patchBusiness } = usePatchBusiness(workspaceId, businessId);

    const handleAccounting = useCallback(data => {
        setAccountingConfiguration(data);
    }, []);

    const handleAssets = useCallback((val, key) => {
        setAsset(prev => ({
            ...prev,
            ...(key === "assetGroup" ? { assetSpecies: null } : {}),
            [key]: val
        }));
    }, []);

    const handleNavigateBack = useCallback(
        businessId => {
            const { pathname } = location;
            const newUrl = pathname.replace(/\/accounting$/, "/details");
            navigate(newUrl);
        },
        [location, navigate]
    );

    useEffect(() => {
        if (businessId && !businessData && !loadingGet) {
            getBusiness?.().then(res => {
                const { asset, accountingConfiguration } = res?.data || {
                    asset: null,
                    accountingConfiguration: null
                };

                const { id, ...accountingConfigurationData } = accountingConfiguration || {
                    id: null
                };

                setBusiness(res?.data);

                if (id && accountingConfiguration) {
                    setAccountingConfiguration(prev => ({
                        ...prev,
                        ...accountingConfigurationData
                    }));
                } else {
                    setAccountingConfiguration(prev => ({
                        ...prev,
                        simplifiedAccountingType: res?.data?.selfEmployed ? "CASH_ACCOUNTING" : "PAYMENTS_PRESUMPTION"
                    }));
                }

                if (asset) {
                    setAsset(prev => ({
                        ...prev,
                        ...asset,
                        statutoryDepreciation: true
                    }));
                }

                setIsEditing(true);
            });
        }
    }, [businessId, getBusiness, businessData, loadingGet, setBusiness]);

    return (
        <>
            {feedback && (
                <WsSnackbar
                    position={"absolute"}
                    sx={{
                        right: "0 !important",
                        top: "-4.5rem !important",
                        width: 700
                    }}
                    type={feedback === Feedback.ERROR ? WsSnackbarTypes.error : WsSnackbarTypes.success}
                    title={t(`business.details.title`)}
                    message={t(`feedback.business.edit.${feedback}`)}
                    horizontal="right"
                    handleClose={() => setFeedback(null)}
                />
            )}
            <BusinessAccountingForm
                asset={asset}
                atecoCode={businessData?.atecoCode}
                selfEmployed={businessData?.selfEmployed}
                accountingConfiguration={accountingConfiguration}
                handleAssets={handleAssets}
                handleAccounting={handleAccounting}
                onSave={() => {
                    const { id } = businessData?.accountingConfiguration ?? {
                        id: null
                    };

                    if (id) {
                        patchBusinessAccounting({
                            url: `rnd:core:workspace-write:api://v1/workspaces/${workspaceId}/business/${businessId}/accounting-configuration/${id}`,
                            data: {
                                ...accountingConfiguration,
                                ...(businessData?.selfEmployed && accountingConfiguration?.accountingType === "ORDINARY"
                                    ? { simplifiedAccountingType: "CASH_ACCOUNTING" }
                                    : {})
                            }
                        })
                            .then(res => {
                                patchBusiness({
                                    data: { asset: { ...asset } }
                                })
                                    .then(res => {
                                        navigate(`/details/${workspaceId}/business/wizard/${businessId}/sectionals`);
                                    })
                                    .catch(() => setFeedback(Feedback.ERROR));
                            })
                            .catch(() => setFeedback(Feedback.ERROR));
                    } else {
                        postBusinessAccounting?.({
                            data: {
                                ...accountingConfiguration,
                                ...(businessData?.selfEmployed && accountingConfiguration?.accountingType === "ORDINARY"
                                    ? { simplifiedAccountingType: "CASH_ACCOUNTING" }
                                    : {})
                            }
                        })
                            .then(res => {
                                patchBusiness({
                                    data: { asset: { ...asset } }
                                })
                                    .then(res => {
                                        navigate(`/details/${workspaceId}/business/wizard/${businessId}/sectionals`);
                                    })
                                    .catch(() => setFeedback(Feedback.ERROR));
                            })
                            .catch(() => setFeedback(Feedback.ERROR));
                    }
                }}
                onCancel={() => handleNavigateBack(businessId)}
                isEditing={isEditing}
                isNewBusiness={true}
                loading={loadingPatchAccounting || loadingPostAccounting || loadingPatchBusiness || loadingGet}
            />
        </>
    );
};
